"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type BeKind,
  type BeMatch,
  type BeReport,
  findBeVerbs,
} from "@/lib/tool-kit/style-checkers/to-be-verbs";

/** English strings. A translated content file overrides these through `ui`. */
export const TO_BE_VERB_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "There are several reasons why the study is important. It is clear that the results were influenced by the sample, which was small. The decision to exclude the data was made by the committee. The new curriculum is a reflection of changing values, and it is an indication of broader reform. Teachers are struggling with the workload, and the schedule is being revised. The policy is in agreement with the guidance.",
  ignoreQuotes: "Ignore quoted text",
  showLabel: "Highlight",
  showAll: "All kinds",
  enterPrompt:
    "Paste or type some text to see its to-be verbs — am, is, are, was, were, be, been and being — and where a stronger verb may be hiding.",
  results: "Results",
  total: "To-be verbs",
  perHundred: "Per 100 words",
  shareWith: "Sentences with one",
  perSentence: "Per sentence",
  words: "Words",
  sentences: "Sentences",
  judgement:
    "A prompt, not a rule: to-be verbs define, describe and build the passive and progressive tenses. Revise the ones that hide who did what; keep the rest.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with to-be verbs highlighted",
  hoverHint:
    "Hover over a highlight to see how the verb is used and, where there is one, the verb to try instead.",
  titleTry: "Try: {replacement}.",
  noneFound: "No to-be verbs found. Your sentences are built on action verbs.",
  nothingShown:
    "No to-be verbs of this kind. Choose “All kinds” to see them all.",
  kindsHeading: "How each to-be verb is used",
  colKind: "Use",
  colCount: "Count",
  colAsk: "What to ask",
  kindLinking: "Linking (is a student, was tired)",
  kindPassive: "Passive (was written, can be seen)",
  kindProgressive: "Progressive (is running)",
  kindExistential: "Existential (there is, there are)",
  kindExpletive: "Dummy “it” (it is clear that…)",
  askLinking:
    "Is there a verb inside the noun or adjective that follows? “Is a reflection of” → “reflects”.",
  askPassive:
    "Who did it? Name the actor with an active verb, unless the actor is unknown or unimportant.",
  askProgressive:
    "Is the simple tense enough? “Is running” → “runs”. Keep the progressive for an action under way now.",
  askExistential:
    "Put the real subject first: “There are many students who struggle” → “Many students struggle”.",
  askExpletive:
    "Say the claim directly: “It is important to note that X” → “X”, or “Note that X”.",
  detoursHeading: "Phrases with a stronger verb inside",
  detoursHint:
    "Each phrase below names an action in a noun or adjective. The verb is a bare form: fit its tense and number to your sentence.",
  colPhrase: "Phrase",
  colTry: "Try",
  colTimes: "Times",
  formsHeading: "To-be verbs by form",
  colForm: "Form",
  heavyHeading: "Sentences to revise first",
  heavyHint:
    "Each of these has three or more to-be verbs — the likeliest places for the action to be buried.",
  beCount: "one: to-be verb|other: to-be verbs",
} as const;

type Strings = typeof TO_BE_VERB_CHECKER_STRINGS;
type Filter = "all" | BeKind;

const TONES: Record<BeKind, HighlightTone> = {
  linking: "amber",
  passive: "violet",
  progressive: "sky",
  existential: "rose",
  expletive: "emerald",
};

const KIND_ORDER: readonly BeKind[] = [
  "linking",
  "passive",
  "progressive",
  "existential",
  "expletive",
];

const KIND_LABEL: Record<BeKind, keyof Strings> = {
  linking: "kindLinking",
  passive: "kindPassive",
  progressive: "kindProgressive",
  existential: "kindExistential",
  expletive: "kindExpletive",
};

const KIND_ASK: Record<BeKind, keyof Strings> = {
  linking: "askLinking",
  passive: "askPassive",
  progressive: "askProgressive",
  existential: "askExistential",
  expletive: "askExpletive",
};

function titleFor(match: BeMatch, strings: Strings): string {
  const ask = strings[KIND_ASK[match.kind]];
  return match.replacement
    ? `${t(strings.titleTry, { replacement: match.replacement })} ${ask}`
    : ask;
}

function Detours({ report, strings }: { report: BeReport; strings: Strings }) {
  const fmt = useToolNumber();
  if (report.replacements.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.detoursHeading}</h3>
      <p className={HINT}>{strings.detoursHint}</p>
      <DataTable
        head={[strings.colPhrase, strings.colTry, strings.colTimes]}
        rows={report.replacements.map((row) => {
          const [phrase, replacement] = row.key.split("|");
          return [phrase, replacement, fmt(row.count)];
        })}
      />
    </div>
  );
}

export default function ToBeVerbCheckerWidget() {
  const strings = useToolUi(TO_BE_VERB_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [ignoreQuotes, setIgnoreQuotes] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => findBeVerbs(deferred, { ignoreQuotes }),
    [deferred, ignoreQuotes]
  );
  const shown = report.matches.filter(
    (match) => filter === "all" || match.kind === filter
  );
  const highlights: TextMatch[] = shown.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.kind,
    title: titleFor(match, strings),
  }));
  const hasText = deferred.trim().length > 0;
  const oneDecimal = { maximumFractionDigits: 1 };
  const percent = { style: "percent", maximumFractionDigits: 0 } as const;
  const filterOptions = [
    { value: "all" as Filter, label: strings.showAll },
    ...KIND_ORDER.map((kind) => ({
      value: kind as Filter,
      label: strings[KIND_LABEL[kind]],
    })),
  ];

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <Checkbox
            checked={ignoreQuotes}
            label={strings.ignoreQuotes}
            onChange={setIgnoreQuotes}
          />
        }
        hint={strings.privacy}
        id="tbv-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.total}
                value={fmt(report.matches.length)}
              />
              <StatCard
                label={strings.perHundred}
                value={fmt(report.perHundred, oneDecimal)}
              />
              <StatCard
                label={strings.shareWith}
                value={fmt(report.shareWith, percent)}
              />
              <StatCard
                label={strings.perSentence}
                value={fmt(report.perSentence, { maximumFractionDigits: 2 })}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
              <StatCard
                label={strings.sentences}
                value={fmt(report.sentences)}
              />
            </StatGrid>
            <p className={HINT}>{strings.judgement}</p>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <div className="max-w-xs">
              <SelectField
                id="tbv-filter"
                label={strings.showLabel}
                onChange={setFilter}
                options={filterOptions}
                value={filter}
              />
            </div>
            <ToneLegend
              items={KIND_ORDER.map((kind) => ({
                tone: TONES[kind],
                label: strings[KIND_LABEL[kind]],
              }))}
            />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.matches.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : null}
          {report.matches.length > 0 && shown.length === 0 ? (
            <Notice>{strings.nothingShown}</Notice>
          ) : null}

          {report.matches.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.kindsHeading}</h3>
              <DataTable
                head={[strings.colKind, strings.colCount, strings.colAsk]}
                rows={KIND_ORDER.map((kind) => [
                  strings[KIND_LABEL[kind]],
                  fmt(report.byKind[kind]),
                  strings[KIND_ASK[kind]],
                ])}
              />
            </div>
          ) : null}

          <Detours report={report} strings={strings} />

          {report.heavy.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.heavyHeading}</h3>
              <p className={HINT}>{strings.heavyHint}</p>
              <ul className="space-y-2">
                {report.heavy.map((sentence) => (
                  <li className={CARD} key={sentence.start}>
                    <p className="text-foreground text-sm leading-relaxed">
                      {sentence.text}
                    </p>
                    <p className="mt-2 font-medium text-muted-foreground text-xs">
                      {fmt(sentence.count)}
                      {tPlural(strings.beCount, locale, sentence.count)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {report.byForm.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.formsHeading}</h3>
              <DataTable
                head={[strings.colForm, strings.colTimes]}
                rows={report.byForm.map((row) => [row.key, fmt(row.count)])}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
